import React, { useState } from "react";
import apiEndpoints from "@store/apiEndpoints";
import errorFunctions from "@store/errorFunctions";

/*
Approve / Decline for a ticket whose status is "... Pending Approval" - the
same flow as the Approve/Decline footer in
Warranties/SingleWarranty/TicketProcess/TicketProcess.js (ApproveModal.js,
DeclineModal.js), on this screen's tn-modal-* shell. Both dialogs make their
own requests and call `onDone(message)` once the ticket has changed, so the
caller only needs to reload it.

Mount them only while open.
*/

const Shell = function (props) {
  const { title, busy, error, onClose, onSubmit, submitLabel, submitDisabled, children } = props;
  return (
    <div className="tn-modal-overlay" onClick={busy ? undefined : onClose}>
      <form
        className="tn-modal tn-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onSubmit={function (e) {
          e.preventDefault();
          if (!busy && !submitDisabled) onSubmit();
        }}
        onClick={function (e) {
          e.stopPropagation();
        }}
      >
        <p className="tn-modal-title">{title}</p>
        {children}
        {error ? <p className="tn-note-error">{error}</p> : null}
        <div className="tn-modal-actions">
          <button type="button" className="tn-btn tn-btn-ghost tn-btn-sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="tn-btn tn-btn-primary tn-btn-sm" disabled={busy || submitDisabled}>
            {busy ? "Working…" : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

/*
Opened by "Advance status" while the ticket is pending approval: PUT
ticket.approve and nothing else. The dialog shows the service center and DOA
the ticket already has, straight from the GET /ticket/{id}/ response the page
loaded - DOA is edited on the Service tab, not here.
Like TicketProcess (which blocks Approve until a service center is chosen),
Approve stays disabled while the ticket has no service center central.
*/
export const ApproveTicketModal = function (props) {
  const { ticket, nextStatus, protectedFetch, onDone, onClose } = props;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const hasCentral = !!ticket.service_center_central;

  const approve = function () {
    setBusy(true);
    setError(null);
    protectedFetch(apiEndpoints.ticket.approve(ticket.id), "PUT", null, errorFunctions.checkResponseSent)
      .then(function (ok) {
        if (!ok) return Promise.reject(null);
        setBusy(false);
        onDone("Ticket status updated", "success");
      })
      .catch(function (err) {
        if (err) console.log("status failed", err);
        setBusy(false);
        setError("Couldn't status the ticket. Please try again.");
      });
  };

  return (
    <Shell
      title="Advance status"
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={approve}
      submitLabel="Advance"
      submitDisabled={!hasCentral}
    >
      <p className="tn-modal-body">
        {nextStatus
          ? 'Move this ticket to the next status "' + nextStatus + '"?'
          : "Are you sure you want to approve this ticket?"}
      </p>
    </Shell>
  );
};

/*
TicketProcess.declineTicket: PUT ticket.reject with the reason, then log
"Reason declined: …" as a ticket note. The note is a second request, so if
only it fails the ticket is still declined - `onDone` says so rather than
reporting the whole decline as failed.
*/
export const DeclineTicketModal = function (props) {
  const { ticket, protectedFetch, onDone, onClose } = props;
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const decline = function () {
    const note = reason.trim();
    setBusy(true);
    setError(null);
    protectedFetch(apiEndpoints.ticket.reject(ticket.id), "PUT", { note: note }, errorFunctions.checkResponseSent)
      .then(function (ok) {
        if (!ok) return Promise.reject(null);
        const formData = new FormData();
        formData.append("note", "Reason declined: " + note);
        return protectedFetch(
          apiEndpoints.ticket.note(ticket.id),
          "PUT",
          formData,
          errorFunctions.checkResponseSent,
          true
        )
          .catch(function () {
            return false;
          })
          .then(function (noteOk) {
            setBusy(false);
            onDone(
              noteOk
                ? "Ticket declined"
                : "Ticket declined, but the reason couldn't be saved as a note.",
              noteOk ? "success" : "warning"
            );
          });
      })
      .catch(function (err) {
        if (err) console.log("decline failed", err);
        setBusy(false);
        setError("Couldn't decline the ticket. Please try again.");
      });
  };

  return (
    <Shell
      title="Decline ticket"
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={decline}
      submitLabel="Decline"
      submitDisabled={!reason.trim()}
    >
      <div className="tn-field">
        <label className="tn-field-label" htmlFor="tnDeclineReason">
          Reason for decline
        </label>
        <textarea
          id="tnDeclineReason"
          className="tn-textarea"
          placeholder="Explain why this ticket is being declined…"
          value={reason}
          onChange={function (e) {
            setReason(e.target.value);
          }}
          disabled={busy}
          autoFocus
        />
        <p className="tn-field-hint">
          Saved with the decline and added to the ticket&apos;s notes.
        </p>
      </div>
    </Shell>
  );
};

export default { ApproveTicketModal, DeclineTicketModal };
